import { toValidDownloadUrl } from '@aibees/shared/utils';
import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import { Icon } from '../icon';
import RejectionFiles from './errors-rejection-files';
import * as SX from './sx';
import { UploadAvatarRef, UploadProps } from './types';

const types = ['jpeg', 'jpg', 'png'];
const accepted = types.map((x) => `.${x}`).join();

export const DEFAULT_ACCEPTED_MESSAGE = types.map((x) => `*.${x}`).join();

const UploadAvatar = forwardRef<UploadAvatarRef, UploadProps>(
  (
    {
      error,
      file,
      disabled,
      helperText,
      showRealRejectReason,
      loading,
      percentage,
      sx,
      ...other
    },
    outerRef
  ) => {
    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragReject,
      fileRejections,
      open,
    } = useDropzone({
      multiple: false,
      disabled,
      accept: {
        [accepted]: [],
      },
      ...other,
    });

    const hasFile = !!file;
    const hasError = isDragReject || !!error;
    const imgUrl = typeof file === 'string' ? file : file?.preview;

    const renderAvatar = (
      <Avatar
        size={56}
        src={hasFile ? toValidDownloadUrl(imgUrl) : undefined}
        variant="rounded"
        color="white"
      >
        <Icon size={24} name="person-outline" color="grey.400" />
      </Avatar>
    );

    const renderPlaceholder = (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        className="upload-placeholder"
        sx={{
          top: 0,
          left: 0,
          width: 56,
          height: 56,
          zIndex: 9,
          borderRadius: 1,
          position: 'absolute',
          color: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create(['opacity'], {
              duration: theme.transitions.duration.shorter,
            }),

          ...(hasError && {
            color: 'error[500]',
            bgcolor: 'error[50]',
          }),
          ...(hasFile && {
            zIndex: 9,
            opacity: 0,
            color: 'common.white',
            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.08),
            '&:hover': {
              opacity: 0.72,
            },
          }),
          ...(!hasFile && {
            opacity: 0,
            color: 'common.white',
            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.2),
            '&:hover': {
              opacity: 1,
            },
          }),
        }}
      >
        <Icon name="camera" size={24} />
      </Stack>
    );

    const renderContent = (
      <Box
        sx={{
          width: 1,
          height: 1,
          overflow: 'hidden',
          borderRadius: 1,
          position: 'relative',
        }}
      >
        {renderAvatar}
        {renderPlaceholder}
      </Box>
    );

    useImperativeHandle(outerRef, () => ({ open }), [open]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          position: 'relative',
        }}
      >
        <Box
          {...getRootProps()}
          sx={{
            m: 'auto',
            width: 56,
            height: 56,
            cursor: 'pointer',
            overflow: 'hidden',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            ...(isDragActive && {
              opacity: 0.72,
            }),
            ...(disabled && {
              opacity: 0.48,
              pointerEvents: 'none',
            }),
            ...(hasError && {
              borderColor: 'error[100]',
            }),
            ...(hasFile && {
              ...(hasError && {
                bgcolor: 'error[50]',
              }),
              '&:hover .upload-placeholder': {
                opacity: 1,
              },
            }),
            ...sx,
          }}
        >
          <input {...getInputProps()} />

          {loading ? (
            <CircularProgress value={percentage || undefined} />
          ) : (
            renderContent
          )}
        </Box>

        {helperText === true && helperText}
        {fileRejections.length > 0 && (
          <Box className="error-wrapper" sx={SX.errorBox}>
            <RejectionFiles
              fileRejections={fileRejections as any}
              showRealRejectReason={showRealRejectReason}
            />
            <Typography variant="body4" color="red" sx={SX.allowedText}>
              Allowed {DEFAULT_ACCEPTED_MESSAGE}.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

export default UploadAvatar;
